// The app's use of the browser's own back/forward history, lifted out of nav-controller.ts
// so that a unit test can load it (df-8458e2ecf8ec).
//
// Two things make that possible, and both are the whole point of this file:
//
//  - Nothing here is reached for from a global. Every browser feature this class uses —
//    the history entries, the popstate event, the two storages, the clock, the timers —
//    arrives as `BrowserEnvironment`, so a test can hand it fakes it controls.
//  - Nothing here imports Angular, a component, or `app/app-page-helpers`. What this class
//    needs from NavController arrives as `BrowserHistoryHost`, which NavController satisfies
//    without being named. Importing nav-controller.ts pulls in the whole component tree, and
//    that is what stopped every earlier attempt to test this code: the tests that exist for
//    it today read its SOURCE as text (browser-back-recovery.test.ts,
//    nav-teardown-during-pop.test.ts) because no test could ever run it.
//
// The behaviour is exactly what it was inside nav-controller.ts. This was a move, not a
// rewrite.

import { DeferredPromise } from '@cf-shared/shared-util';
import { describeErrorForLog } from '@cf-shared/error-describe';
import { BackTimeoutAction, resolveBackTimeoutAction } from './browser-back-recovery';

/**
 * What the app records on each of the browser's own history entries, so that a back or
 * forward press tells us which page it means rather than only which address.
 */
export interface NavigationState {
  url: string;
  breadcrumbs: string;
  pageNames?: string[];
}

/**
 * What the app writes to one history entry: the address, the pages it stands for (root
 * first), and the trail of the pages under the last one. Every writer records each page by
 * its registered name (getPageName), never its address segment, so writeEntriesForOpenedPage
 * can compare an existing entry's last page with one name.
 */
export interface HistoryEntry {
  url: string;
  pageNames: string[];
  breadcrumbs: string;
}

/**
 * What writeEntriesForOpenedPage did. The values are the strings that reach Sentry and
 * Mixpanel, so renaming one renames it in the reports too.
 */
export enum OpenedPageHistoryOutcome {
  /** the ordinary case: the current entry rewritten as the parent, one entry added */
  EntryWritten = 'entry-written',
  /** more than one entry added, because a page under the opened one had none yet */
  CaughtUp = 'caught-up',
  /** nothing written: a later page's write had already added this page's entry */
  AlreadyCovered = 'already-covered',
  /** the current entry's depth could not be trusted, so the plain pair was written */
  FellBackToPair = 'fell-back-to-pair',
  /** nothing written, because a browser navigation was being handled */
  RefusedDuringBrowserNavigation = 'refused-during-browser-navigation',
}

export interface OpenedPageHistoryWrite {
  outcome: OpenedPageHistoryOutcome;
  /** how many entries were added to the browser's history */
  entriesAdded: number;
}

// if the app was backgrounded for longer than this, discard the persisted URL
// and reset to home screen (prevents resuming on a stale date or deep flow)
export const WARM_RESUME_URL_MAX_AGE_MS = 4 * 60 * 60 * 1000; // 4 hours

// How long resetAndPushStackAsync waits for the browser to report that it has stepped
// back before rebuilding anyway. A healthy browser answers in a few milliseconds, so this
// is only ever paid when it fires no popstate at all — which it does when its current
// entry is already the oldest it holds (#4707). Long enough to be a safety net rather
// than a guess, short enough that the rebuild never looks like a hang.
export const HISTORY_RESET_TIMEOUT_MS = 1000;

/** How long after our own back finishes a stray popstate is treated as its late duplicate. */
export const DUPLICATE_POPSTATE_WINDOW_MS = 300;

/** How long navigateBackAsync waits for the browser before deciding the back never happened. */
export const BACK_NAVIGATION_TIMEOUT_MS = 5000;

/** The part of `localStorage` this file uses. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * A timer handle, kept opaque on purpose: the real browser and a test's fake clock hand
 * back different things, and nothing here does anything with the value except give it
 * back to `clearTimer`.
 */
export type TimerHandle = unknown;

/**
 * Every browser feature this file uses. Production passes `realBrowserEnvironment()`; a
 * test passes fakes, which is what lets it drive a whole sequence of history moves.
 */
export interface BrowserEnvironment {
  /** What the browser recorded on the history entry it is showing right now. */
  historyState(): unknown;
  /** How many entries the browser is holding. Reported in telemetry, never acted on. */
  historyLength(): number;
  pushState(state: unknown, title: string, url: string): void;
  replaceState(state: unknown, title: string, url: string): void;
  back(): void;
  go(delta: number): void;
  addPopStateListener(listener: (event: PopStateEvent) => void): void;
  removePopStateListener(listener: (event: PopStateEvent) => void): void;
  localStorage: StorageLike;
  /** What the page reports about being visible, or undefined where there is no document. */
  visibilityState(): string | undefined;
  now(): number;
  setTimer(handler: () => void, ms: number): TimerHandle;
  clearTimer(handle: TimerHandle): void;
}

/** The two telemetry calls this file makes. A subset of NavController's own logger. */
export interface NavHistoryLogger {
  trackSilentProblem(name: string, context: unknown): void;
  trackSilentInfo(name: string, context: unknown): void;
}

/** The one breadcrumb call this file makes. NavigationBreadcrumbService satisfies it. */
export interface BreadcrumbEncoder {
  encodeBreadcrumbs(pageStack: { pageName: string; params: unknown }[]): string;
}

/** The one address call this file makes. Angular's Location satisfies it. */
export interface CurrentAddress {
  path(): string;
}

/** The one address call this file makes on the app's own URL rules. UrlService satisfies it. */
export interface UrlNormalizer {
  normalizeUrl(url: string): string;
}

/** The overlay calls this file makes. OverlayStackService satisfies it. */
export interface OverlayStack {
  hasActiveOverlay(): boolean;
  dismissTopOverlay(): void;
}

/**
 * What this file needs from NavController. NavController satisfies it structurally, so
 * neither file has to import the other and the component tree stays on the far side.
 */
export interface BrowserHistoryHost {
  getCurrentPage(): string;
  /**
   * The URL segment of the page on screen, or an empty answer when there is none. Kept on
   * the host because working it out needs `app/app-page-helpers`, whose import tree is the
   * reason this file could not be tested before.
   */
  getCurrentPageSegment(): string;
  getNavLogger(): NavHistoryLogger | undefined;
  handleBrowserNavigation(url: string, state: NavigationState | null): Promise<void>;
  restoreBlockedNavigation(): void;
  flushPostBrowserNavCallbacks(): void;
  recoverStuckBackNavigationAsync(): Promise<void>;
  /** The navigation component, absent until it is wired up. Only its presence is read. */
  readonly nav: unknown;
  /** How many pages the app has open. Only meaningful once `nav` is there. */
  length(): number;
}

const WARM_RESUME_URL_KEY = 'warm_resume_url';
const WARM_RESUME_URL_TS_KEY = 'warm_resume_url_ts';

/**
 * Persist the current URL to localStorage so warm resume can restore it after process kill.
 * Only persists non-trivial URLs (not '/').
 */
export function persistUrlForWarmResume(env: BrowserEnvironment, url: string) {
  if (url && url !== '/') {
    env.localStorage.setItem(WARM_RESUME_URL_KEY, url);
    env.localStorage.setItem(WARM_RESUME_URL_TS_KEY, String(env.now()));
  }
}

/**
 * Get the persisted URL for warm resume, or null if stale/missing.
 * Discards the URL if it was persisted more than WARM_RESUME_URL_MAX_AGE_MS ago.
 */
export function getPersistedUrl(env: BrowserEnvironment): string | null {
  const url = env.localStorage.getItem(WARM_RESUME_URL_KEY);
  if (!url) return null;

  // if timestamp is missing (legacy entry from before staleness check) or expired
  const ts = Number(env.localStorage.getItem(WARM_RESUME_URL_TS_KEY));
  if (!ts || env.now() - ts > WARM_RESUME_URL_MAX_AGE_MS) {
    env.localStorage.removeItem(WARM_RESUME_URL_KEY);
    env.localStorage.removeItem(WARM_RESUME_URL_TS_KEY);
    return null;
  }
  return url;
}

/**
 * Forget the remembered address, so the next launch starts at the default page instead of
 * reopening whatever was last on screen. Used when that address turned out to be a page the
 * app cannot show — without this, every relaunch for the next few hours goes straight back
 * to it (#4681).
 */
export function clearPersistedUrl(env: BrowserEnvironment) {
  env.localStorage.removeItem(WARM_RESUME_URL_KEY);
  env.localStorage.removeItem(WARM_RESUME_URL_TS_KEY);
}

/**
 * The real browser. Every member reads its global when it is CALLED rather than when this
 * object is built, so building one is safe at any point in boot.
 */
export function realBrowserEnvironment(): BrowserEnvironment {
  return {
    historyState: () => history.state,
    historyLength: () => history.length,
    pushState: (state, title, url) => history.pushState(state, title, url),
    replaceState: (state, title, url) => history.replaceState(state, title, url),
    back: () => history.back(),
    go: (delta) => history.go(delta),
    addPopStateListener: (listener) => window.addEventListener('popstate', listener),
    removePopStateListener: (listener) => window.removeEventListener('popstate', listener),
    get localStorage() { return localStorage; },
    visibilityState: () => typeof document !== 'undefined' ? document.visibilityState : undefined,
    now: () => Date.now(),
    setTimer: (handler, ms) => setTimeout(handler, ms),
    clearTimer: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
  };
}

/**
 * What BrowserHistory is doing with a page transition right now. Exactly one holds at a time,
 * so a combination the code can never be in cannot be written down (df-4521eb27fd90).
 */
export enum NavState {
  /** nothing in flight */
  Idle = 'idle',
  /** navigateBackAsync called env.back() and is waiting for the browser's popstate */
  AwaitingAppBackPop = 'awaiting-app-back-pop',
  /**
   * onPopState is handling a popstate. This is the user's own back press AND our app back's
   * popstate alike; the two are told apart by `pendingBack`, never by this state.
   */
  HandlingPopState = 'handling-popstate',
  /**
   * navigateBackAsync timed out with no popstate and is closing the page itself (#4707).
   * NOT "from the browser": NavController relies on isFromBrowser() being false here.
   */
  RecoveringStuckBack = 'recovering-stuck-back',
}

/**
 * One in-flight operation that a later event must answer, and that only the call which
 * started it may stand down.
 *
 * A second operation of the same kind can start while the first is still waiting (a
 * return callback flushed at the end of a back navigation can start its own; a second
 * stack rebuild can start behind the first). The first call must then NOT clear the marker
 * when it wakes, because the marker now belongs to the second. `releaseIfMine` is that rule,
 * written once for both back navigation and the history reset (df-4521eb27fd90).
 */
export class InFlightMarker {
  private current: DeferredPromise<void> | null = null;

  /** Start a new operation, replacing any earlier one, and return its own handle. */
  claim(): DeferredPromise<void> {
    const own = DeferredPromise.defer<void>();
    this.current = own;
    return own;
  }

  /** Whether an operation is in flight and has not been released yet. */
  get isHeld(): boolean {
    return this.current !== null;
  }

  /** Answer the operation but leave the marker held, for its owner to release. */
  signal(): void {
    this.current?.resolve(undefined);
  }

  /** Answer the operation and clear the marker, whoever owns it. */
  settleAndRelease(): void {
    if (this.current) {
      this.current.resolve(undefined);
      this.current = null;
    }
  }

  /** Clear the marker only while it is still `own`. Returns whether it was. */
  releaseIfMine(own: DeferredPromise<void>): boolean {
    if (this.current !== own) {
      return false;
    }
    this.current = null;
    return true;
  }
}

/**
 * Class for managing browser history API interactions.
 * This is owned and managed by NavController.
 */
export class BrowserHistory {
  private navState = NavState.Idle;
  /** Held from navigateBackAsync's env.back() until its popstate has been handled. */
  private pendingBack = new InFlightMarker();
  /** Last state pushed/replaced — used to restore position after overlay dismiss on browser back */
  private lastKnownState: NavigationState | null = null;
  private lastKnownUrl: string = '';
  private _lastAppPopCompleteTime = 0;
  // Whether onPopState actually began processing our app-initiated back. Reset in
  // navigateBackAsync before env.back(); set true at the top of onPopState for an
  // app-initiated pop. If the 5s safety timeout wins with this still false, the
  // popstate was never delivered (a real, silently-lost pop) rather than merely slow.
  private pendingBackHandlerStarted = false;

  constructor(
    private host: BrowserHistoryHost,
    private overlayStack: OverlayStack,
    private location: CurrentAddress,
    private serializer: UrlNormalizer,
    private breadcrumbService: BreadcrumbEncoder,
    private env: BrowserEnvironment
  ) {}

  /**
   * Initialize the browser history handler.
   */
  private popstateListener: ((event: PopStateEvent) => void) | null = null;
  init() {
    this.popstateListener = (event: PopStateEvent) => void this.onPopState(event);
    this.env.addPopStateListener(this.popstateListener);

    // Store initial state
    const initialUrl = this.serializer.normalizeUrl(this.location.path());
    const currentPage = this.host.getCurrentPage();
    const initialTrail = currentPage ? this.breadcrumbService.encodeBreadcrumbs([{
      pageName: this.host.getCurrentPageSegment() || currentPage,
      params: {}
    }]) : '';

    const initialState: NavigationState = {
      url: initialUrl,
      breadcrumbs: initialTrail,
      pageNames: [currentPage]
    };

    // Replace initial state to have our metadata
    this.env.replaceState(initialState, '', initialUrl);
    this.lastKnownState = initialState;
    this.lastKnownUrl = initialUrl;
  }

  destroy() {
    if (this.popstateListener) {
      this.env.removePopStateListener(this.popstateListener);
      this.popstateListener = null;
    }
  }

  /**
   * Handle browser popstate events (back/forward button).
   * Priority: Overlays → Page Navigation
   */
  private async onPopState(event: PopStateEvent) {
    // Ignore popstate during history reset
    if (this.pendingHistoryReset.isHeld) {
      // This is also the traversal's completion signal. resetAndPushStackAsync asked the
      // browser to step back and is waiting right here for it to say it has done so —
      // without this the wait is a fixed guess, and a browser slower than the guess gets
      // its answer read as a user pressing back.
      this.resolveHistoryReset();
      this.resolvePendingBack();
      return;
    }

    // Prevent re-entrant calls
    if (this.isTransitionRunning()) {
      // The event is dropped — two page transitions cannot run at once. But the browser
      // has ALREADY consumed a history entry to deliver it, and dropping it without
      // putting the position back is what walks the browser down towards the oldest
      // entry it has. Once it gets there, a back does nothing at all and fires
      // no popstate, so every close button in the app goes dead with no message and no
      // way out but force-quitting (#4707).
      //
      // The position is restored for EVERY event that reaches here, because every kind
      // of event that CAN reach here wants the same treatment — each one consumed a
      // history entry without a page being popped for it. There are three kinds:
      //
      //  - a fresh user press landing while a handler is running;
      //  - the delayed duplicate popstate an app-initiated back can produce,
      //    which the duplicate-popstate guard further down already restores for;
      //  - the app's own back turning up very late, during the five-second recovery in
      //    navigateBackAsync. That is the one window where the state is RecoveringStuckBack
      //    and pendingBack is still held, so resolvePendingBack() below is what claims it —
      //    neither that call nor the pendingBack the recovery holds is dead code.
      //
      // pendingBack is NOT the discriminator it looks like, and gating the restore on it
      // was this fix's first, wrong shape. resolvePendingBack() runs only in this
      // handler's finally, so a real user press landing mid-transition still sees it set
      // — which is precisely the sequence that walks the browser down.
      this.resolvePendingBack();
      this.host.restoreBlockedNavigation();
      return;
    }

    // Track whether this popstate was triggered by our own navigateBackAsync()
    // (app-initiated: popAsync → navigateBackAsync → env.back()).
    // pendingBack is held from inside navigateBackAsync before it calls env.back().
    const isAppInitiated = this.pendingBack.isHeld;

    // guard against spurious duplicate popstate events — an app-initiated
    // back can produce a delayed second popstate (observed in CI Chrome
    // with gaps up to ~155ms). If a non-app-initiated popstate arrives within
    // DUPLICATE_POPSTATE_WINDOW_MS of completing an app-initiated pop, treat it as
    // spurious: restore browser history to the correct state and ignore the event.
    // App-initiated popstate (pendingBack is held) always passes through, because
    // deferred callbacks can legitimately trigger another popAsync immediately
    // after a pop completes.
    if (!isAppInitiated && this.env.now() - this._lastAppPopCompleteTime < DUPLICATE_POPSTATE_WINDOW_MS) {
      console.log('NavController: Ignoring likely duplicate popstate (' +
        (this.env.now() - this._lastAppPopCompleteTime) + 'ms since last app-initiated pop)');
      this.host.restoreBlockedNavigation();
      return;
    }

    this.navState = NavState.HandlingPopState;

    // Record that the popstate for our app-initiated back was actually delivered and
    // is now being processed (before any await below, some of which — e.g. an
    // ionViewCanLeave "discard changes?" dialog — can legitimately run for longer than
    // navigateBackAsync's 5s safety timeout). This lets that timeout tell a
    // never-delivered popstate apart from one that is merely slow.
    if (isAppInitiated) {
      this.pendingBackHandlerStarted = true;
    }

    try {
      // Priority 1: if an overlay is open, close it instead of navigating.
      // Only for browser-initiated back (user pressing browser back / swipe gesture).
      // Skip for app-initiated back — the app navigation flow handles ionViewCanLeave
      // and any alerts itself. Checking here would race: the alert popover created by
      // ionViewCanLeave could appear just before this popstate fires, causing us to
      // dismiss it and block the navigation that was already in progress.
      if (!isAppInitiated && this.overlayStack.hasActiveOverlay()) {
        console.log('NavController: popstate dismissed overlay instead of navigating');
        this.overlayStack.dismissTopOverlay();
        // Browser already went back one real history entry — push the last
        // known state back so the URL and nav stack stay in sync (overlay
        // close shouldn't change the page)
        if (this.lastKnownState && this.lastKnownUrl) {
          this.env.pushState(this.lastKnownState, '', this.lastKnownUrl);
        }
        return;
      }

      // Priority 2: Handle page navigation
      const newUrl = this.serializer.normalizeUrl(this.location.path());
      const state = event.state as NavigationState;

      // Let NavController handle the actual page navigation
      await this.host.handleBrowserNavigation(newUrl, state);

    } finally {
      this.navState = NavState.Idle;
      this.resolvePendingBack();
      // flush return callbacks that were deferred from popAsync_internal —
      // now safe because the state is Idle, so pushAsync from callbacks
      // can correctly update browser history via BrowserHistory.pushState
      this.host.flushPostBrowserNavCallbacks();
      // only track app-initiated pop timing — spurious duplicate popstate only
      // follows an app-initiated back, not user browser-back presses
      if (isAppInitiated) {
        this._lastAppPopCompleteTime = this.env.now();
      }
    }
  }

  /**
   * Signal resetAndPushStackAsync that the traversal it asked for has happened.
   */
  private resolveHistoryReset() {
    // Resolve only — the waiting call clears the field itself, once it has checked the
    // reset is still its own. Clearing it here would make that ownership check read as
    // "somebody else owns this now" and leave the ignore-popstate flag set for ever.
    this.pendingHistoryReset.signal();
  }

  /**
   * Report that the history reset's traversal was never confirmed within
   * HISTORY_RESET_TIMEOUT_MS. The browser fires no popstate at all when it is already on
   * the oldest entry it holds, so the stack is rebuilt on top of a history position that
   * does not describe it — the same dead position #4707 is about. Nothing else reports it.
   */
  private reportHistoryResetTimeout(requestedDelta: number) {
    const context = {
      requestedDelta,
      currentPath: this.location.path(),
      lastKnownUrl: this.lastKnownUrl,
      historyLength: this.env.historyLength(),
      pageDepth: (this.env.historyState() as NavigationState | null)?.pageNames?.length,
      navDepth: this.currentNavDepth(),
    };

    const logger = this.host.getNavLogger();
    if (!logger) {
      // Logger not wired yet (very early boot) — never fully silent.
      console.error('BrowserHistory: history reset traversal was never confirmed', context);
      return;
    }
    logger.trackSilentProblem('NavHistoryResetTimedOut', context);
  }

  /**
   * Signal navigateBackAsync that popstate processing is complete.
   */
  private resolvePendingBack() {
    this.pendingBack.settleAndRelease();
  }

  /**
   * Push a new state to browser history.
   */
  pushState(url: string, pageStack: string[], breadcrumbs: string) {
    if (this.navState === NavState.HandlingPopState) {
      return;
    }

    this.pushRestoredEntry(url, pageStack, breadcrumbs);
  }

  /**
   * Push a history entry back after the browser consumed one but no page transition
   * matched it — a navigation ionViewCanLeave blocked, an event dropped because another
   * transition was already running, or a duplicate popstate.
   *
   * Same three pieces of bookkeeping as pushState and replaceState: write the entry, keep
   * our record of the on-screen address truthful, and persist that address for warm resume.
   *
   * DELIBERATELY NOT guarded by the HandlingPopState state, which is what makes this a separate
   * method rather than a call to pushState. That guard exists to stop an app navigation
   * competing with a browser-driven pop that is still running. This push is not a
   * competing navigation — it is the correction OF that pop, so it has to happen during
   * exactly the window the guard closes. Route it through pushState instead and it becomes
   * a silent no-op at the one call site it exists for: NavController.handleBrowserNavigation
   * runs with the state already HandlingPopState (df-3d9c1692ab6c).
   */
  pushRestoredEntry(url: string, pageStack: string[], breadcrumbs: string) {
    const state: NavigationState = {
      url: url,
      breadcrumbs: breadcrumbs,
      pageNames: [...pageStack]
    };

    this.env.pushState(state, '', url);
    this.lastKnownState = state;
    this.lastKnownUrl = url;

    // persist URL for warm resume (survives process kill via localStorage)
    persistUrlForWarmResume(this.env, url);
  }

  /**
   * Give back the entries a browser back went past without closing their pages (#6411).
   *
   * A back that arrives while a page is still opening lands one entry lower than the page the
   * user sees, because that page has no entry yet. NavController closes only the opening page,
   * so the pages between the entry the browser landed on and the top of the stack are still
   * open but have no entry the browser stands on. This rewrites the landed entry and pushes one
   * entry for each of those pages, so the browser again holds one entry per open page and stands
   * on the top one.
   *
   * Like pushRestoredEntry, it is deliberately NOT refused while a popstate is being handled: it
   * is the correction of that very navigation, and its only caller runs inside it.
   *
   * @param entries one entry per open page, root first.
   * @param landedDepth how many pages the entry the browser landed on stands for.
   * @returns how many entries were pushed.
   */
  restoreEntriesAboveLandedEntry(entries: HistoryEntry[], landedDepth: number): number {
    if (landedDepth < 1 || landedDepth > entries.length) {
      throw new Error(`BrowserHistory: landed depth ${landedDepth} is outside the ${entries.length} open pages`);
    }
    const landed = entries[landedDepth - 1];
    this.replaceState(landed.url, landed.pageNames, landed.breadcrumbs);
    for (let index = landedDepth; index < entries.length; index++) {
      const entry = entries[index];
      this.pushRestoredEntry(entry.url, entry.pageNames, entry.breadcrumbs);
    }
    return entries.length - landedDepth;
  }

  /**
   * Make sure the browser holds one history entry for every open page from the root up to
   * and including a page that has just finished opening, counting from the depth its current
   * entry already stands for (#6122). Why a fixed pair is not enough, the fallback, and the
   * all-or-nothing rule: `design-docs/navigation.md`, "One browser history entry per open
   * page".
   *
   * It never writes for pages above the opened one: a page higher up may still be refused by
   * its own ionViewCanEnter, and an entry for it would leave the browser one entry ahead.
   *
   * @param entries one entry per open page, root first.
   * @param openedIndex where the page that just opened sits in that list.
   */
  writeEntriesForOpenedPage(entries: HistoryEntry[], openedIndex: number): OpenedPageHistoryWrite {
    if (openedIndex < 0 || openedIndex >= entries.length) {
      throw new Error(`BrowserHistory: opened page index ${openedIndex} is outside the ${entries.length} open pages`);
    }
    if (this.navState === NavState.HandlingPopState) {
      return { outcome: OpenedPageHistoryOutcome.RefusedDuringBrowserNavigation, entriesAdded: 0 };
    }

    // count from the current entry only while its depth can be trusted; otherwise fall back to
    // the plain pair, which is what every write did before #6122
    const currentDepth = this.trustedCurrentEntryDepth(entries);
    const depthUsable = currentDepth != null;
    if (depthUsable && currentDepth >= openedIndex + 1) {
      return { outcome: OpenedPageHistoryOutcome.AlreadyCovered, entriesAdded: 0 };
    }
    const firstMissing = depthUsable ? currentDepth : openedIndex;

    // rewrite the current entry to describe the pages it now stands for, then add one entry
    // for every page from there up to the opened page
    if (firstMissing > 0) {
      const current = entries[firstMissing - 1];
      this.replaceState(current.url, current.pageNames, current.breadcrumbs);
    }
    for (let index = firstMissing; index <= openedIndex; index++) {
      const entry = entries[index];
      this.pushState(entry.url, entry.pageNames, entry.breadcrumbs);
    }

    const entriesAdded = openedIndex - firstMissing + 1;
    const outcome = !depthUsable ? OpenedPageHistoryOutcome.FellBackToPair
      : entriesAdded > 1 ? OpenedPageHistoryOutcome.CaughtUp
        : OpenedPageHistoryOutcome.EntryWritten;
    return { outcome, entriesAdded };
  }

  /**
   * How many open pages the browser's current history entry stands for, or undefined when
   * that entry cannot be trusted to describe the open pages.
   *
   * The entry counts only while it really describes the open pages up to its depth: within
   * the open stack, and naming as its last page the page open at that depth. A browser-forward
   * press rebuilds the stack without writing any history, which leaves an entry describing
   * pages that are no longer open, at any depth. An entry an older app version wrote with
   * address segments instead of registered names also fails this check.
   *
   * It reads the browser's own current entry, not lastKnownState: that only follows the app's
   * writes, and a browser move it never saw would leave it describing the wrong entry.
   *
   * @param entries one entry per open page, root first.
   */
  trustedCurrentEntryDepth(entries: HistoryEntry[]): number | undefined {
    const recordedPages = (this.env.historyState() as NavigationState | null)?.pageNames;
    const depth = recordedPages?.length;
    const trusted = depth != null && depth >= 1 && depth <= entries.length
      && entries[depth - 1].pageNames[depth - 1] === recordedPages[depth - 1];
    return trusted ? depth : undefined;
  }

  /**
   * Replace current state in browser history.
   */
  replaceState(url: string, pageStack: string[], breadcrumbs: string) {
    const state: NavigationState = {
      url: url,
      breadcrumbs: breadcrumbs,
      pageNames: [...pageStack]
    };

    this.env.replaceState(state, '', url);
    this.lastKnownState = state;
    this.lastKnownUrl = url;

    // persist URL for warm resume (survives process kill via localStorage)
    persistUrlForWarmResume(this.env, url);
  }

  /**
   * Navigate back programmatically.
   */
  async navigateBackAsync() {
    if (this.navState === NavState.HandlingPopState) {
      console.debug('BrowserHistory: Skipping history.back during browser navigation');
      return;
    }

    // if a previous navigateBackAsync is still pending (double-click before popstate fires),
    // skip to prevent multiple back calls that over-pop the nav stack
    if (this.pendingBack.isHeld) {
      console.log('BrowserHistory: Skipping concurrent navigateBackAsync (previous still pending)');
      return;
    }

    // Mark as app-initiated — but never out of RecoveringStuckBack. A back started while the
    // recovery's pop is still running (possible once a late popstate has released pendingBack)
    // must leave popstates dropped until the recovery ends, or its popstate is handled as a
    // second pop on top of the recovery's own.
    if (this.navState !== NavState.RecoveringStuckBack) {
      this.navState = NavState.AwaitingAppBackPop;
    }

    // Reset the handler-started flag for this back navigation. onPopState sets it
    // true when it begins processing our pop; if the safety timeout below wins with
    // this still false, the popstate was never delivered.
    this.pendingBackHandlerStarted = false;

    // Wait for the popstate handler to fully complete before returning, keeping a local
    // handle on the deferred as well as storing it. A deferred return callback flushed at the end
    // of the recovery below can start its OWN back navigation, which puts a fresh
    // deferred in this.pendingBack — and the cleanup at the end of this method must not
    // clear a marker belonging to that nested call. Clearing it made onPopState read the
    // nested pop as not-app-initiated, so its popstate was eaten by the duplicate guard
    // and the page never closed.
    const ownPendingBack = this.pendingBack.claim();

    // This will trigger popstate event, which will call our handler
    this.env.back();

    // Race the real popstate completion against a 5s safety timeout, resolving to a
    // discriminant so we can tell which branch won. A back navigation that silently
    // failed to happen must surface (project rule: unexpected conditions must
    // surface); previously both outcomes were indistinguishable and the failure was
    // invisible in production.
    let timeoutId: TimerHandle | undefined;
    const outcome = await Promise.race<'completed' | 'timeout'>([
      ownPendingBack.then(() => 'completed' as const),
      new Promise<'timeout'>(resolve => {
        timeoutId = this.env.setTimer(() => resolve('timeout'), BACK_NAVIGATION_TIMEOUT_MS);
      }),
    ]);
    // Clear the safety timer once the race is settled (a no-op if it already fired).
    if (timeoutId !== undefined) {
      this.env.clearTimer(timeoutId);
    }

    if (outcome === 'timeout') {
      this.reportBackNavigationTimeout();

      const action = resolveBackTimeoutAction({
        handlerStarted: this.pendingBackHandlerStarted,
        navDepth: this.currentNavDepth(),
      });
      if (action === BackTimeoutAction.RecoverByDirectPop) {
        // The browser fired no popstate, so nothing removed the page and the user is
        // looking at a screen whose close button does nothing — and will keep doing
        // nothing, because the next press hits the same dead history position. Close
        // the page ourselves (#4707).
        //
        // Keep this back navigation marked as in flight ACROSS the recovery. The pop
        // below is a full page transition and can put an ionViewCanLeave dialog on
        // screen, so it is not quick. An object that looked idle for that whole window
        // would let a second press of X start a SECOND back navigation on top of the one
        // already running, costing the user an extra page: pendingBack left held is what
        // makes navigateBackAsync refuse that, and RecoveringStuckBack is what makes onPopState
        // drop a popstate that turns up late rather than treat it as a fresh press.
        // Both are cleared in the finally, never before the await.
        this.navState = NavState.RecoveringStuckBack;
        try {
          await this.host.recoverStuckBackNavigationAsync();
        } catch (err) {
          // The recovery is the last thing standing between the user and a page they
          // cannot close, so a failure here has to be visible. Without this it becomes
          // an unhandled rejection travelling back out through popAsync into page code
          // that voids it, which is the silent-absorption shape the project forbids.
          const logger = this.host.getNavLogger();
          if (logger) {
            logger.trackSilentProblem('NavBackRecoveryFailed', { error: describeErrorForLog(err) });
          } else {
            console.error('BrowserHistory: back-navigation recovery failed', err);
          }
        } finally {
          // onPopState's finally is what normally returns to Idle, and it never ran.
          this.navState = NavState.Idle;
          // open the duplicate-popstate guard's window from the moment the pop
          // finished, so a popstate arriving just after it is absorbed rather than
          // processed as our own pop a second time
          this._lastAppPopCompleteTime = this.env.now();
          this.pendingBack.releaseIfMine(ownPendingBack);

          // Flush the deferred return callbacks LAST, once every marker above is
          // released — the ordering is the whole point of doing it here rather than
          // inside the recovery. popAsync_internal defers those callbacks whenever it is
          // told the pop came from the browser, because normally the popstate handler
          // flushes them when it finishes; there is no popstate handler here, so nothing
          // else ever would. And a parent's returnCallback is allowed to pop again —
          // FoodsPage in select mode does exactly that, through returnSelectedFoodAsync —
          // so flushing while the markers were still set had navigateBackAsync refuse
          // that pop with only a console line, leaving FoodsPage on screen and its own
          // caller unanswered.
          this.host.flushPostBrowserNavCallbacks();
        }
      }
    }

    // Clear the in-flight back marker, but only while it is still the one THIS call put
    // there. A nested back navigation started by a flushed return callback replaces it,
    // and clearing that one would strand the nested call: onPopState would read its
    // popstate as not-app-initiated, the duplicate guard would eat it, and the page it was
    // closing would stay on screen until a second five-second timeout fired (#4707).
    this.pendingBack.releaseIfMine(ownPendingBack);
  }

  /** How many pages the app has open, or 0 before the nav component is wired up. */
  private currentNavDepth(): number {
    // reportBackNavigationTimeout below runs at very early boot too, and must never throw
    return this.host.nav ? this.host.length() : 0;
  }

  /**
   * Report that an app-initiated back navigation did not complete within the 5s
   * safety timeout. Distinguishes the two very different causes:
   *  - the popstate handler never started → the popstate was never delivered
   *    (e.g. a back with a single history entry does nothing, fires no
   *    popstate). This is a real, unresolved failure → problem tier.
   *  - the handler started but had not finished → almost always a slow user-facing
   *    ionViewCanLeave dialog the user sat on for more than 5s. Healthy → info tier.
   */
  private reportBackNavigationTimeout() {
    const context = {
      handlerStarted: this.pendingBackHandlerStarted,
      currentPath: this.location.path(),
      lastKnownUrl: this.lastKnownUrl,
      historyLength: this.env.historyLength(),
      pageDepth: (this.env.historyState() as NavigationState | null)?.pageNames?.length,
      // how many pages the APP has open. Read beside pageDepth (how many the browser's
      // current history entry describes), this is what tells a browser sitting on a
      // stale history position apart from an app that simply had nothing to pop.
      navDepth: this.currentNavDepth(),
      // the two fields the navState enum replaced, kept under their old names and values
      // so log and Sentry searches on these reports read the same as before
      isNavigating: this.isTransitionRunning(),
      navigationSource: this.legacyNavigationSource(),
      visibilityState: this.env.visibilityState(),
    };

    const logger = this.host.getNavLogger();
    if (!logger) {
      // Logger not wired yet (very early boot) — never fully silent.
      console.error('BrowserHistory: navigateBackAsync timed out before logger was ready', context);
      return;
    }

    if (this.pendingBackHandlerStarted) {
      // The handler is running but slow — expected when a canLeave dialog is open.
      logger.trackSilentInfo('NavBackTimedOutHandlerRunning', context);
    } else {
      // The popstate never arrived — the pop silently did not happen.
      logger.trackSilentProblem('NavBackTimedOut', context);
    }
  }

  /**
   * Held while resetAndPushStackAsync waits for its traversal; onPopState ignores popstate
   * while it is held, and signals it when the reset's own traversal finally reports back.
   */
  private pendingHistoryReset = new InFlightMarker();

  /**
   * Reset browser history and push a fresh stack.
   * Goes back to clear old entries, then pushes new ones.
   */
  async resetAndPushStackAsync(
    pages: { url: string; pageNames: string[]; breadcrumbs: string }[]
  ): Promise<void> {
    if (pages.length === 0) return;

    // Get current depth from state
    const currentState = this.env.historyState() as NavigationState | null;
    const currentDepth = currentState?.pageNames?.length || 1;

    // if we have more than 1 level of history, go back to root
    if (currentDepth > 1) {
      // Wait for the traversal to actually finish, rather than guessing how long it
      // takes. A history move only ASKS the browser to move; the popstate that says it has
      // moved arrives whenever the browser gets to it. This used to wait a fixed 50ms and
      // then stop ignoring popstate — so a browser that answered later had its answer
      // read as the user pressing back, which popped pages straight back out of the stack
      // being rebuilt here.
      const ownReset = this.pendingHistoryReset.claim();

      // Go back to the first entry
      this.env.go(-(currentDepth - 1));

      let timeoutId: TimerHandle | undefined;
      const outcome = await Promise.race<'completed' | 'timeout'>([
        ownReset.then(() => 'completed' as const),
        new Promise<'timeout'>(resolve => {
          timeoutId = this.env.setTimer(() => resolve('timeout'), HISTORY_RESET_TIMEOUT_MS);
        }),
      ]);
      if (timeoutId !== undefined) {
        this.env.clearTimer(timeoutId);
      }

      // Stand down only while this call is still the one the reset belongs to. A second
      // rebuild that started while this one was waiting owns the marker now, and clearing it
      // here would reopen the popstate window in the middle of that one.
      const stillOurs = this.pendingHistoryReset.releaseIfMine(ownReset);

      if (outcome === 'timeout' && stillOurs) {
        // The browser never reported the move. It does that when its current entry is
        // already the oldest one it holds, which is the same dead history position #4707
        // is about — so this has to be visible rather than silently waited out.
        this.reportHistoryResetTimeout(currentDepth - 1);
      }
    }

    // Now push fresh entries for the new stack
    // First page replaces current (root) entry
    const firstPage = pages[0];
    this.replaceState(firstPage.url, firstPage.pageNames, firstPage.breadcrumbs);

    // Remaining pages get pushed
    for (let i = 1; i < pages.length; i++) {
      const page = pages[i];
      this.pushState(page.url, page.pageNames, page.breadcrumbs);
    }
  }

  /**
   * Check if we're currently resetting history (should ignore popstate).
   */
  isResetting(): boolean {
    return this.pendingHistoryReset.isHeld;
  }

  /**
   * Check if currently navigating from browser action.
   */
  isFromBrowser(): boolean {
    return this.navState === NavState.HandlingPopState;
  }

  /** Whether a page transition is running, so a popstate arriving now must be dropped. */
  private isTransitionRunning(): boolean {
    return this.navState === NavState.HandlingPopState || this.navState === NavState.RecoveringStuckBack;
  }

  /** The value the old `navigationSource` field held in the current state, for reports. */
  private legacyNavigationSource(): 'browser' | 'app' | null {
    switch (this.navState) {
      case NavState.HandlingPopState:
        return 'browser';
      case NavState.AwaitingAppBackPop:
      case NavState.RecoveringStuckBack:
        return 'app';
      case NavState.Idle:
        return null;
    }
  }
}
